import type {
  FlowDefinitionPayload,
  FDNodeDefinition,
  FDEdgeDefinition,
  EdgeCondition,
  FlowGetResponse,
} from '../types';

// ── Visual Node Types ──
export type VisualNodeType =
  | 'start'
  | 'llm'
  | 'condition'
  | 'human_input'
  | 'extraction'
  | 'classification'
  | 'bigquery_save'
  | 'lead_enrichment'
  | 'estimate_budget'
  | 'http_request';

// ── Flow Node (recursive model) ──
export interface FlowNode {
  id: string;
  type: VisualNodeType;
  label: string;
  subtitle?: string;
  config: Record<string, unknown>;
  branches?: FlowNode[][]; // Only for condition nodes — each branch is an array of nodes
}

// ── Layout Output Types ──
export interface LayoutItem {
  node: FlowNode;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ConnectorLine {
  id: string;
  from: { x: number; y: number };
  to: { x: number; y: number };
  label?: string;
}

export interface LayoutResult {
  items: LayoutItem[];
  connectors: ConnectorLine[];
  totalWidth: number;
  totalHeight: number;
}

// ── Layout Constants ──
export const CARD_WIDTH = 200;
export const CARD_HEIGHT = 80;
export const H_GAP = 100;
export const V_GAP = 40;

// ── Node Style Config ──
export interface NodeStyleConfig {
  color: string;
  bgColor: string;
  borderColor: string;
  icon: string; // Ant Design icon name
  tagLabel: string;
}

export const NODE_STYLES: Record<VisualNodeType, NodeStyleConfig> = {
  start: {
    color: '#52c41a',
    bgColor: '#f6ffed',
    borderColor: '#b7eb8f',
    icon: 'PlayCircleOutlined',
    tagLabel: 'Inicio',
  },
  llm: {
    color: '#1677ff',
    bgColor: '#e6f4ff',
    borderColor: '#91caff',
    icon: 'RobotOutlined',
    tagLabel: 'LLM',
  },
  condition: {
    color: '#faad14',
    bgColor: '#fffbe6',
    borderColor: '#ffe58f',
    icon: 'BranchesOutlined',
    tagLabel: 'Condicion',
  },
  human_input: {
    color: '#eb2f96',
    bgColor: '#fff0f6',
    borderColor: '#ffadd2',
    icon: 'UserOutlined',
    tagLabel: 'Input Humano',
  },
  extraction: {
    color: '#722ed1',
    bgColor: '#f9f0ff',
    borderColor: '#d3adf7',
    icon: 'FileSearchOutlined',
    tagLabel: 'Extraccion',
  },
  classification: {
    color: '#13c2c2',
    bgColor: '#e6fffb',
    borderColor: '#87e8de',
    icon: 'TagsOutlined',
    tagLabel: 'Clasificacion',
  },
  bigquery_save: {
    color: '#52c41a',
    bgColor: '#f6ffed',
    borderColor: '#b7eb8f',
    icon: 'DatabaseOutlined',
    tagLabel: 'BigQuery',
  },
  lead_enrichment: {
    color: '#fa8c16',
    bgColor: '#fff7e6',
    borderColor: '#ffd591',
    icon: 'UserSearchOutlined',
    tagLabel: 'Lead Lookup',
  },
  estimate_budget: {
    color: '#a0d911',
    bgColor: '#fcffe6',
    borderColor: '#eaff8f',
    icon: 'CalculatorOutlined',
    tagLabel: 'Presupuesto',
  },
  http_request: {
    color: '#0958d9',
    bgColor: '#e6f4ff',
    borderColor: '#91caff',
    icon: 'ApiOutlined',
    tagLabel: 'HTTP',
  },
};

// ── Mapping Visual -> Backend ──
export type BackendNodeType =
  | 'extraction'
  | 'search'
  | 'validation'
  | 'review'
  | 'classification'
  | 'bigquery_save'
  | 'persistence'
  | 'custom';

export function mapVisualToBackend(
  node: FlowNode
): { type: BackendNodeType; name: string; prompt?: string; config: Record<string, unknown> } | null {
  if (node.type === 'start') return null; // Start is visual-only

  const base = {
    name: node.label,
    config: { ...node.config, visual_type: node.type },
  };

  switch (node.type) {
    case 'llm':
      return {
        ...base,
        type: 'custom' as BackendNodeType,
        prompt: (node.config.prompt as string) || undefined,
      };
    case 'condition':
      return {
        ...base,
        type: 'custom' as BackendNodeType,
      };
    case 'human_input':
      return {
        ...base,
        type: 'custom' as BackendNodeType,
      };
    case 'extraction':
      return {
        ...base,
        type: 'extraction' as BackendNodeType,
        prompt: (node.config.prompt as string) || undefined,
      };
    case 'classification':
      return {
        ...base,
        type: 'classification' as BackendNodeType,
        prompt: (node.config.prompt as string) || undefined,
      };
    case 'bigquery_save':
      return {
        ...base,
        type: 'bigquery_save' as BackendNodeType,
      };
    case 'lead_enrichment':
      return {
        ...base,
        type: 'custom' as BackendNodeType,
        prompt: (node.config.prompt as string) || undefined,
      };
    case 'estimate_budget':
      return {
        ...base,
        type: 'custom' as BackendNodeType,
      };
    case 'http_request':
      return {
        ...base,
        type: 'custom' as BackendNodeType,
      };
    default:
      return null;
  }
}

// ── Helper: generate unique id ──
export function generateNodeId(): string {
  return `node_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

// ── Helper: create default flow (just a start node) ──
export function createDefaultFlow(): FlowNode[] {
  return [
    {
      id: generateNodeId(),
      type: 'start',
      label: 'Inicio',
      config: {},
    },
  ];
}

// ── Convert backend nodes to visual FlowNodes ──
// Maps backend node type + config.visual_type to VisualNodeType
function resolveVisualType(backendType: string, config: Record<string, unknown>): VisualNodeType {
  const visualType = config.visual_type as string | undefined;
  if (
    visualType === 'llm' ||
    visualType === 'condition' ||
    visualType === 'human_input' ||
    visualType === 'extraction' ||
    visualType === 'classification' ||
    visualType === 'bigquery_save' ||
    visualType === 'lead_enrichment' ||
    visualType === 'estimate_budget' ||
    visualType === 'http_request'
  ) {
    return visualType;
  }
  // Fallback: map known backend types to visual equivalents
  switch (backendType) {
    case 'extraction':
    case 'search':
    case 'validation':
    case 'review':
    case 'classification':
    case 'persistence':
      return 'llm'; // Show backend-native types as LLM nodes visually
    case 'custom':
      return 'llm';
    default:
      return 'llm';
  }
}

export interface BackendNodeData {
  id: string;
  type: string;
  name: string;
  prompt: string | null;
  config: Record<string, unknown>;
}

export function backendNodesToFlow(backendNodes: BackendNodeData[]): FlowNode[] {
  const startNode: FlowNode = {
    id: generateNodeId(),
    type: 'start',
    label: 'Inicio',
    config: {},
  };

  const flowNodes: FlowNode[] = backendNodes.map((bn) => {
    const visualType = resolveVisualType(bn.type, bn.config);

    const node: FlowNode = {
      id: bn.id,
      type: visualType,
      label: bn.name,
      config: { ...bn.config },
    };

    // Set subtitle based on type
    switch (visualType) {
      case 'llm':
        node.subtitle = (bn.config.model as string) || bn.type;
        if (bn.prompt) {
          node.config.prompt = bn.prompt;
        }
        break;
      case 'condition':
        node.subtitle = `${bn.config.field || ''} ${bn.config.operator || 'equals'} ${bn.config.value || ''}`.trim();
        node.branches = [[], []];
        break;
      case 'human_input':
        node.subtitle = bn.prompt || undefined;
        break;
      case 'extraction': {
        const schema = bn.config.output_schema;
        if (Array.isArray(schema) && schema.length > 0) {
          node.subtitle = (schema as { field: string }[]).map((f) => f.field).filter(Boolean).join(', ');
        }
        if (bn.prompt) node.config.prompt = bn.prompt;
        break;
      }
      case 'classification': {
        const cats = bn.config.categories;
        if (Array.isArray(cats) && cats.length > 0) {
          node.subtitle = (cats as string[]).join(', ');
        }
        if (bn.prompt) node.config.prompt = bn.prompt;
        break;
      }
      case 'bigquery_save':
        node.subtitle = 'flow_executions';
        break;
    }

    return node;
  });

  return [startNode, ...flowNodes];
}

// ── FlowNode[] tree → FlowDefinitionPayload (flat nodes + edges) ──

function generateEdgeId(): string {
  return `edge_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function flowNodesToFlowDefinition(
  flowNodes: FlowNode[]
): FlowDefinitionPayload {
  const nodeDefs: FDNodeDefinition[] = [];
  const edgeDefs: FDEdgeDefinition[] = [];

  function processSequence(
    nodes: FlowNode[]
  ): { firstId: string | null; lastId: string | null } {
    let firstId: string | null = null;
    let prevId: string | null = null;

    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];

      if (node.type === 'start') {
        prevId = '__start__';
        continue;
      }

      // Create NodeDefinition
      const nodeDef: FDNodeDefinition = {
        id: node.id,
        type: 'custom',
        name: node.label,
        prompt: (node.config.prompt as string) || undefined,
        config: { ...node.config, visual_type: node.type },
      };
      nodeDefs.push(nodeDef);

      if (firstId === null) firstId = node.id;

      // Plain edge from previous node
      if (prevId !== null) {
        edgeDefs.push({
          id: generateEdgeId(),
          source: prevId,
          target: node.id,
        });
      }

      // Handle condition branches
      if (node.type === 'condition' && node.branches) {
        const continuationId =
          i + 1 < nodes.length && nodes[i + 1].type !== 'start'
            ? nodes[i + 1].id
            : '__end__';

        // True branch (index 0) — conditional edge
        if (node.branches[0] && node.branches[0].length > 0) {
          const trueBranch = processSequence(node.branches[0]);
          if (trueBranch.firstId) {
            const condition: EdgeCondition = {
              field: (node.config.field as string) || '',
              operator: (node.config.operator as string) || 'equals',
              value: (node.config.value as string) || '',
            };
            edgeDefs.push({
              id: generateEdgeId(),
              source: node.id,
              target: trueBranch.firstId,
              condition,
            });
            // Connect end of true branch to continuation
            if (trueBranch.lastId && trueBranch.lastId !== continuationId) {
              edgeDefs.push({
                id: generateEdgeId(),
                source: trueBranch.lastId,
                target: continuationId,
              });
            }
          }
        }

        // False branch (index 1) — default (no condition)
        if (node.branches[1] && node.branches[1].length > 0) {
          const falseBranch = processSequence(node.branches[1]);
          if (falseBranch.firstId) {
            edgeDefs.push({
              id: generateEdgeId(),
              source: node.id,
              target: falseBranch.firstId,
            });
            // Connect end of false branch to continuation
            if (falseBranch.lastId && falseBranch.lastId !== continuationId) {
              edgeDefs.push({
                id: generateEdgeId(),
                source: falseBranch.lastId,
                target: continuationId,
              });
            }
          }
        } else {
          // Empty false branch → direct default edge to continuation
          edgeDefs.push({
            id: generateEdgeId(),
            source: node.id,
            target: continuationId,
          });
        }

        // Branches handle routing — skip setting prevId
        prevId = null;
        continue;
      }

      prevId = node.id;
    }

    return { firstId, lastId: prevId };
  }

  const result = processSequence(flowNodes);

  // Add final edge to __end__ if needed
  if (result.lastId && result.lastId !== '__start__') {
    const hasEndEdge = edgeDefs.some(
      (e) => e.source === result.lastId && e.target === '__end__'
    );
    if (!hasEndEdge) {
      edgeDefs.push({
        id: generateEdgeId(),
        source: result.lastId,
        target: '__end__',
      });
    }
  }

  return { nodes: nodeDefs, edges: edgeDefs };
}

// ── FlowDefinition (flat nodes + edges) → FlowNode[] tree ──

export function flowDefinitionToFlowNodes(
  flowDef: FlowGetResponse['flow_definition']
): FlowNode[] {
  const { nodes, edges } = flowDef;

  // Build lookup maps
  const nodeMap = new Map<string, FDNodeDefinition>();
  for (const n of nodes) nodeMap.set(n.id, n);

  const edgesBySource = new Map<string, FDEdgeDefinition[]>();
  for (const e of edges) {
    const arr = edgesBySource.get(e.source) || [];
    arr.push(e);
    edgesBySource.set(e.source, arr);
  }

  // Find merge point: follow each branch to find where they converge
  function findMergePoint(conditionNodeId: string): string | null {
    const condEdges = edgesBySource.get(conditionNodeId) || [];
    const branchStarts = condEdges.map((e) => e.target);

    const branchEnds: string[] = [];
    for (const startId of branchStarts) {
      let current = startId;
      while (current && current !== '__end__') {
        const outEdges = (edgesBySource.get(current) || []).filter(
          (e) => !e.condition
        );
        if (outEdges.length === 1) {
          current = outEdges[0].target;
        } else {
          break;
        }
      }
      branchEnds.push(current);
    }

    // If both branches converge to the same node, that's the merge point
    if (branchEnds.length >= 2 && branchEnds[0] === branchEnds[1]) {
      return branchEnds[0];
    }

    return branchEnds[0] || null;
  }

  // Convert a NodeDefinition to a FlowNode
  function toFlowNode(nodeDef: FDNodeDefinition): FlowNode {
    const config = nodeDef.config || {};
    const visualType = resolveVisualType(nodeDef.type, config);

    const node: FlowNode = {
      id: nodeDef.id,
      type: visualType,
      label: nodeDef.name,
      config: { ...config },
    };

    if (visualType === 'llm') {
      node.subtitle = (config.model as string) || nodeDef.type;
      if (nodeDef.prompt) node.config.prompt = nodeDef.prompt;
    } else if (visualType === 'condition') {
      node.subtitle = `${config.field || ''} ${config.operator || 'equals'} ${config.value || ''}`.trim();
    } else if (visualType === 'human_input') {
      node.subtitle = nodeDef.prompt || undefined;
    } else if (visualType === 'extraction') {
      const schema = config.output_schema;
      if (Array.isArray(schema) && schema.length > 0) {
        node.subtitle = (schema as { field: string }[]).map((f) => f.field).filter(Boolean).join(', ');
      }
      if (nodeDef.prompt) node.config.prompt = nodeDef.prompt;
    } else if (visualType === 'classification') {
      const cats = config.categories;
      if (Array.isArray(cats) && cats.length > 0) {
        node.subtitle = (cats as string[]).join(', ');
      }
      if (nodeDef.prompt) node.config.prompt = nodeDef.prompt;
    } else if (visualType === 'bigquery_save') {
      node.subtitle = 'flow_executions';
    }

    return node;
  }

  // Follow a chain from startId until mergeId (exclusive)
  function followChain(startId: string, mergeId: string | null): FlowNode[] {
    const result: FlowNode[] = [];
    let currentId: string | null = startId;

    while (currentId && currentId !== '__end__' && currentId !== mergeId) {
      const nodeDef = nodeMap.get(currentId);
      if (!nodeDef) break;

      const outEdges: FDEdgeDefinition[] = edgesBySource.get(currentId) || [];
      const hasConditional = outEdges.some((e: FDEdgeDefinition) => e.condition);

      if (hasConditional) {
        // Condition node
        const flowNode = toFlowNode(nodeDef);
        flowNode.type = 'condition';

        const condMerge = findMergePoint(currentId);

        const trueEdge = outEdges.find((e: FDEdgeDefinition) => e.condition);
        const falseEdge = outEdges.find((e: FDEdgeDefinition) => !e.condition);

        const trueBranch = trueEdge
          ? followChain(trueEdge.target, condMerge)
          : [];
        const falseBranch = falseEdge
          ? followChain(falseEdge.target, condMerge)
          : [];

        flowNode.branches = [trueBranch, falseBranch];
        result.push(flowNode);

        // Continue from merge point
        currentId = condMerge;
      } else {
        // Plain node
        const flowNode = toFlowNode(nodeDef);
        result.push(flowNode);

        const outPlain: FDEdgeDefinition[] = outEdges.filter((e: FDEdgeDefinition) => !e.condition);
        currentId = outPlain.length === 1 ? outPlain[0].target : null;
      }
    }

    return result;
  }

  // Build from __start__
  const startEdges = edgesBySource.get('__start__') || [];
  const firstNodeId =
    startEdges.length > 0 ? startEdges[0].target : null;

  const startNode: FlowNode = {
    id: generateNodeId(),
    type: 'start',
    label: 'Inicio',
    config: {},
  };

  if (!firstNodeId || firstNodeId === '__end__') {
    return [startNode];
  }

  const mainChain = followChain(firstNodeId, null);
  return [startNode, ...mainChain];
}
